import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Badge
} from '@mui/material';
import {
  Business,
  LocalHospital,
  ExpandMore,
  SwapHoriz,
  AccessTime,
  CheckCircle,
  Info,
  Add,
  CalendarToday
} from '@mui/icons-material';
import { useEnterpriseAuth } from '../context/EnterpriseAuthContext';
import { apiUtils } from '../utils/apiUtils';
import { useNotification } from '../context/NotificationContext';

export default function FacilitySelector() {
  const { user, updateToken } = useEnterpriseAuth();
  const { notify: showSnackbar } = useNotification();
  const [anchorEl, setAnchorEl] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [currentFacility, setCurrentFacility] = useState(null);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [facilityContext, setFacilityContext] = useState(null);

  useEffect(() => {
    loadFacilities();
  }, []);

  const loadFacilities = async () => {
    setLoading(true);
    try {
      const response = await apiUtils.get('/api/patient/facilities');
      if (response.success) {
        setFacilities(response.facilities);
        setCurrentFacility(response.currentFacility);
      }
    } catch (error) {
      console.error('Error loading facilities:', error);
      showSnackbar('Failed to load facilities', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFacilitySwitch = async (facilityId) => {
    if (facilityId === currentFacility?.facility_id) {
      showSnackbar('Already at this facility', 'info');
      return;
    }

    setSwitching(true);
    try {
      const response = await apiUtils.post('/api/patient/facilities/switch', {
        targetFacilityId: facilityId
      });

      if (response.success) {
        // Update the token with new facility context
        if (response.token) {
          updateToken(response.token);
        }
        
        setCurrentFacility(response.facility);
        showSnackbar(`Switched to ${response.facility.name}`, 'success');
        
        // Reload the page to refresh context
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error) {
      console.error('Error switching facility:', error);
      showSnackbar('Failed to switch facility', 'error');
    } finally {
      setSwitching(false);
      setAnchorEl(null);
    }
  };

  const loadFacilityContext = async (facilityId) => {
    try {
      const response = await apiUtils.get(`/api/patient/facilities/${facilityId}/context`);
      if (response.success) {
        setFacilityContext(response);
      }
    } catch (error) {
      console.error('Error loading facility context:', error);
    }
  };

  const handleShowDetails = async (facility) => {
    setSelectedFacility(facility);
    setDetailsDialog(true);
    await loadFacilityContext(facility.facility_id);
  };

  const handleRequestAccess = () => {
    // Navigate to access request page
    window.location.href = '/patient/request-facility-access';
  };

  const getFacilityIcon = (type) => {
    switch (type) {
      case 'hospital':
        return <LocalHospital />;
      case 'asc':
        return <Business />;
      default:
        return <Business />;
    }
  };

  const getFacilityTypeLabel = (type) => {
    switch (type) {
      case 'hospital':
        return 'Hospital';
      case 'asc':
        return 'Surgery Center';
      default:
        return 'Facility';
    }
  };

  // Don't show selector if user doesn't have multi-facility access
  if (!user?.federation?.canSwitchFacilities) {
    return null;
  }

  if (loading) {
    return <CircularProgress size={24} />;
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={getFacilityIcon(currentFacility?.facility_type)}
          endIcon={<ExpandMore />}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          disabled={switching}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            minWidth: 200
          }}
        >
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="caption" display="block" color="text.secondary">
              Current Facility
            </Typography>
            <Typography variant="body2">
              {currentFacility?.facility_name || 'Select Facility'}
            </Typography>
          </Box>
        </Button>

        {user?.federation?.currentSurgeryContext && (
          <Chip
            icon={<CalendarToday />}
            label={`Surgery: ${new Date(user.federation.currentSurgeryContext.scheduledDate).toLocaleDateString()}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: { width: 350, maxHeight: 400 }
        }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Your Facilities
          </Typography>
        </Box>
        
        <Divider />
        
        {facilities.map((facility) => (
          <MenuItem
            key={facility.facility_id}
            selected={facility.facility_id === currentFacility?.facility_id}
            sx={{ py: 1.5 }}
          >
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getFacilityIcon(facility.facility_type)}
                  <Typography variant="body1">
                    {facility.facility_name}
                  </Typography>
                  {facility.facility_id === currentFacility?.facility_id && (
                    <Chip label="Current" size="small" color="primary" />
                  )}
                </Box>
              }
              secondary={
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {getFacilityTypeLabel(facility.facility_type)} • 
                    {facility.city}, {facility.state}
                  </Typography>
                  {facility.upcoming_surgeries > 0 && (
                    <Badge 
                      badgeContent={facility.upcoming_surgeries} 
                      color="error"
                      sx={{ ml: 2 }}
                    >
                      <Typography variant="caption" color="warning.main">
                        Upcoming procedures
                      </Typography>
                    </Badge>
                  )}
                </Box>
              }
            />
            <ListItemSecondaryAction>
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  handleShowDetails(facility);
                }}
              >
                <Info />
              </IconButton>
              {facility.facility_id !== currentFacility?.facility_id && (
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFacilitySwitch(facility.facility_id);
                  }}
                  disabled={switching}
                >
                  <SwapHoriz />
                </IconButton>
              )}
            </ListItemSecondaryAction>
          </MenuItem>
        ))}
        
        <Divider />
        
        <MenuItem onClick={handleRequestAccess}>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Add />
                <Typography variant="body2">
                  Request Access to New Facility
                </Typography>
              </Box>
            }
          />
        </MenuItem>
      </Menu>

      {/* Facility Details Dialog */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getFacilityIcon(selectedFacility?.facility_type)}
            {selectedFacility?.facility_name}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedFacility && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Facility Information
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {selectedFacility.address}<br />
                {selectedFacility.city}, {selectedFacility.state} {selectedFacility.zip_code}<br />
                Phone: {selectedFacility.phone}
              </Typography>

              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Your Activity
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="First Visit"
                    secondary={new Date(selectedFacility.first_visit_date).toLocaleDateString()}
                  />
                </ListItem>
                {selectedFacility.last_visit_date && (
                  <ListItem>
                    <ListItemText 
                      primary="Last Visit"
                      secondary={new Date(selectedFacility.last_visit_date).toLocaleDateString()}
                    />
                  </ListItem>
                )}
                <ListItem>
                  <ListItemText 
                    primary="Total Visits"
                    secondary={selectedFacility.total_visits}
                  />
                </ListItem>
              </List>

              {facilityContext?.surgeryContexts?.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Procedures at this Facility
                  </Typography>
                  <List dense>
                    {facilityContext.surgeryContexts.map((context, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={context.procedure_type}
                          secondary={
                            <>
                              {new Date(context.scheduled_date).toLocaleDateString()} • 
                              Status: {context.status}
                            </>
                          }
                        />
                        {context.status === 'scheduled' && (
                          <CheckCircle color="success" fontSize="small" />
                        )}
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>
            Close
          </Button>
          {selectedFacility?.facility_id !== currentFacility?.facility_id && (
            <Button
              variant="contained"
              startIcon={<SwapHoriz />}
              onClick={() => {
                setDetailsDialog(false);
                handleFacilitySwitch(selectedFacility.facility_id);
              }}
              disabled={switching}
            >
              Switch to This Facility
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}